import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import moment from 'moment';
import WaiverWorkItemListComponent, { DefaultFieldMap } from './WaiverWorkItemListComponent';
import WaiverWorkItemSearchCriteriaComponent from './WaiverWorkItemSearchCriteriaComponent';
import PageTitle from '../../pages/components/pageTitle';
import AirlineSnoozeStatus from './AirlineSnoozeStatus';
import styles from './work-item-list.module.css';
import { Tabset, TabList, NavTab, TabContent, TabPanel } from '../../components/tabs/Tabs';
import { WaiverWorkItemView, WaiverWorkItemSearchCriteria, WaiverWorkItemsListType, Resolution, WaiverWorkItem } from '../../types/waiverWorkItem';
import { setHelpPage, setWindowTitle } from '../../configTdm';
import { Provider, useSelector } from 'react-redux';
import store from '../../redux/store';
import { getAllWaiverConfigurations, snoozeWaiverConfiguration } from '../../service/waiverConfigurationService';

import { Waiver, WaiverAirline, WaiverConfiguration } from '../../types/waiver';
import { WaiverWorkItemSearchCriteriaState } from '../../redux/components/waivers/workItemsSearch/criteriaReducer';
import { closeSpinner, spinnerShow } from '../../lib/spinner';
import { getWaivers } from '../../service/waiverService';
import { getWaiverAirlines } from '../../service/waiverAirlineService';
import { getWaiverWorkItems } from '../../service/waiverWorkItemService';
import _ from 'lodash';
import { selectResolutions } from '../../redux/entities/waivers/workItems/waiverWorkItemResolutionsSelectors';
import { State } from '../../redux/types';

const initialState: WaiverWorkItemSearchCriteriaState = {
  ACTIVE_WORK_ITEMS: {
    listType: 'ACTIVE_WORK_ITEMS',
    state: 'OPEN',
  },
  HISTORICAL_WORK_ITEMS: {
    listType: 'HISTORICAL_WORK_ITEMS',
    state: 'CLOSED',
    dateRange: {
      startDate: moment().subtract(29, 'days'),
      endDate: moment(),
    },
  },
};

const WaiverWorkItemListContainer: React.FC = () => {
  const resolutions = useSelector((state: State) => selectResolutions(state));
  const [waivers, setWaivers] = useState<Record<number, Waiver>>({});
  const [airlines, setAirlines] = useState<WaiverAirline[]>([]);
  const [waiverConfigurations, setWaiverConfigurations] = useState<WaiverConfiguration[]>([]);
  const [waiverWorkItems, setWaiverWorkItems] = useState<WaiverWorkItem[]>([]);
  const [waiverWorkItemViews, setWaiverWorkItemViews] = useState<WaiverWorkItemView[]>([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState<WaiverWorkItemSearchCriteria>(initialState['ACTIVE_WORK_ITEMS']);
  const [listType, setListType] = useState<WaiverWorkItemsListType>('ACTIVE_WORK_ITEMS');
  const [snoozeLoading, setSnoozeLoading] = useState(false);
  const navigate = useNavigate();
  const routeTo = (href: string) => {
    navigate(href);
  };

  useEffect(() => {
    spinnerShow();
    setLoading(true);
    setWindowTitle('Waiver Work Items');
    setHelpPage('helpWaiverWorkItemList');
    (async () => {
      try {
        const [waivers, airlines, waiverConfigurations, waiverWorkItems] = await Promise.all([
          getWaivers(),
          getWaiverAirlines(),
          getAllWaiverConfigurations(),
          getWaiverWorkItems(searchCriteria),
        ]);

        setWaivers(_.keyBy(waivers, 'id'));
        setAirlines(airlines);
        setWaiverConfigurations(waiverConfigurations);
        setWaiverWorkItems(waiverWorkItems);
        setLoading(false);
        closeSpinner();
      } catch (error) {
        setErrorMsg('Error loading data.');
        setLoading(false);
        closeSpinner();
      }
    })();
  }, []);

  const changeListType = async (listType: WaiverWorkItemsListType) => {
    spinnerShow();
    setLoading(true);
    setListType(listType);
    if (searchCriteria.listType !== listType) {
      setSearchCriteria(initialState[listType]);
    }
    try {
      const workItems = await getWaiverWorkItems(initialState[listType]);
      setWaiverWorkItems(workItems);
      if (workItems.length === 0) {
        setLoading(false);
        closeSpinner();
        setWaiverWorkItemViews([]);
      }
    } catch (error) {
      setErrorMsg('Error loading data.');
      setLoading(false);
      closeSpinner();
    }
  }

  const handleCriteriaChange = async (searchCriteria: WaiverWorkItemSearchCriteria) => {
    spinnerShow();
    setLoading(true);
    setSearchCriteria(searchCriteria);
    try {
      const workItems = await getWaiverWorkItems(searchCriteria);
      setWaiverWorkItems(workItems);
      if (workItems.length === 0) {
        setLoading(false);
        closeSpinner();
        setWaiverWorkItemViews([]);
      }
    } catch (error) {
      setErrorMsg('Error loading data.');
      setLoading(false);
      closeSpinner();
    }
  }

  useEffect(() => {
    if (waiverWorkItems.length && Object.keys(waivers).length) {
      const views = waiverWorkItems.map((workItem) => {
        const associatedWaiverIds = workItem.associatedWaiverIds;
        const associatedWaivers = Array.from(associatedWaiverIds).map((waiverId) => {
          return waivers[waiverId];
        });
        return {
          ...workItem,
          associatedWaivers: associatedWaivers as Waiver[],
        };
      });
      setWaiverWorkItemViews(views);
      setLoading(false);
      closeSpinner();
    }
  }, [waiverWorkItems, waivers]);

  const cancelAirlineSnooze = async (airlineCode: string) => {
    setSnoozeLoading(true);
    try {
      const response = await snoozeWaiverConfiguration(airlineCode, 0);
      if (response) {
        setWaiverConfigurations(waiverConfigurations.map((config) => {
          if (config.waiverTemplate.issuingAirlineCode === airlineCode) {
            return {
              ...response,
            };
          }
          return config;
        }));
      }
      setSnoozeLoading(false);
    } catch (error) {
      setSnoozeLoading(false);
    }
  }

  return (
    <div className={cn(styles.contentWrapper)}>
      <PageTitle>
        WAIVER ACTION ITEMS
        <AirlineSnoozeStatus
          snoozedWaiverConfigurations={waiverConfigurations.filter((config) => config.snoozeNotificationsUntil &&
            moment(config.snoozeNotificationsUntil).isAfter(moment()))}
          loading={snoozeLoading}
          now={moment()}
          onSnoozeCancel={(airlineCode: string) => cancelAirlineSnooze(airlineCode)}
          className={styles.waiverListAirlineSnoozeStatus}
        />
      </PageTitle>
      <Tabset>
        <TabList>
          <NavTab
            name="Waiver Action Items"
            active={listType === 'ACTIVE_WORK_ITEMS'}
            onSelect={() => changeListType('ACTIVE_WORK_ITEMS')}
          />
          <NavTab
            name="Historical Work Items"
            active={listType === 'HISTORICAL_WORK_ITEMS'}
            onSelect={() => changeListType('HISTORICAL_WORK_ITEMS')}
          />
        </TabList>
        <TabContent>
          <TabPanel active={listType === 'ACTIVE_WORK_ITEMS'}>
            <WaiverWorkItemListComponent
              waiverWorkItemViews={waiverWorkItemViews}
              errorMsg={errorMsg}
              navigateTo={routeTo}
              loading={loading}
            />
          </TabPanel>
          <TabPanel active={listType === 'HISTORICAL_WORK_ITEMS'}>
            <WaiverWorkItemSearchCriteriaComponent
              airlines={airlines || []}
              resolutions={resolutions || []}
              searchCriteria={searchCriteria}
              onChange={(searchCriteria: WaiverWorkItemSearchCriteria) => handleCriteriaChange(searchCriteria)}
            />
            <WaiverWorkItemListComponent
              fieldMap={{
                ...DefaultFieldMap,
                Resolution: (workItemView: WaiverWorkItemView) => workItemView.resolution,
              }}
              waiverWorkItemViews={waiverWorkItemViews}
              errorMsg={errorMsg}
              navigateTo={routeTo}
              loading={loading}
            />
          </TabPanel>
        </TabContent>
      </Tabset>
    </div>
  );
};

const WrappedWaiverWorkItemListContainer: React.FC = () => (
  <Provider store={store}>
    <WaiverWorkItemListContainer />
  </Provider>
);

export default WrappedWaiverWorkItemListContainer;